import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import AppHeader from "../components/AppHeader";
import { Search, FilePlus2, Scale, Loader2, ArrowRight } from "lucide-react";

// Panel de control — muestra las últimas valoraciones (reemplaza el antiguo
// "Histórico de informes" que dependía de /api/reports, ya retirado).

const STATUS_COLORS = {
  borrador: "bg-stone-200 text-stone-700",
  extraccion_pendiente: "bg-amber-100 text-amber-800 border border-amber-200",
  revision_pendiente: "bg-orange-100 text-orange-800 border border-orange-200",
  comparables_pendientes: "bg-blue-100 text-blue-800 border border-blue-200",
  valorado: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  informe_generado: "bg-[#E0651F] text-white",
  archivado: "bg-stone-100 text-stone-500 line-through",
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) +
      " · " + d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [meta, setMeta] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async (query = "") => {
    setLoading(true);
    setErr("");
    try {
      const params = new URLSearchParams({ limit: "20" });
      if (query) params.set("q", query);
      const [c, m] = await Promise.all([
        api.get(`/admin/valoraciones/cases?${params.toString()}`),
        meta ? Promise.resolve({ data: meta }) : api.get(`/admin/valoraciones/meta`),
      ]);
      setCases(c.data.cases || []);
      setTotal(c.data.total || 0);
      setMeta(m.data);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Error cargando");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onSearch = (e) => { e.preventDefault(); load(q); };

  return (
    <div className="min-h-screen bg-stone-50 grain-bg" data-testid="admin-dashboard">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-stone-500 mb-3">Panel de control</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-none text-[#2d1f1a]">
              Últimas valoraciones.
            </h1>
            <p className="text-stone-500 text-sm mt-3 max-w-lg leading-relaxed">
              {total} {total === 1 ? "expediente" : "expedientes"} en el sistema. Búsqueda por cliente, ID de subasta, dirección o referencia catastral.
            </p>
          </div>
          <Link
            to="/admin/valoraciones"
            className="inline-flex items-center gap-2 bg-[#2d1f1a] hover:bg-[#E0651F] text-white px-5 py-3 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
            data-testid="new-report-cta"
          >
            <FilePlus2 className="w-4 h-4" /> Nueva valoración
          </Link>
        </div>

        <form onSubmit={onSearch} className="mb-6 flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por cliente, ID subasta, dirección o ref. catastral…"
              className="w-full pl-10 pr-3 py-2.5 border border-stone-300 rounded-sm bg-white focus:outline-none focus:border-[#E0651F] focus:ring-1 focus:ring-[#E0651F] text-sm"
              data-testid="search-input"
            />
          </div>
          <button type="submit" className="px-5 py-2.5 bg-white border border-stone-300 hover:border-[#2d1f1a] text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors" data-testid="search-btn">
            Buscar
          </button>
        </form>

        {err && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-sm text-sm">{err}</div>
        )}

        <div className="bg-white border border-stone-200 rounded-sm shadow-sm">
          {loading ? (
            <div className="p-16 flex items-center justify-center text-stone-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
            </div>
          ) : cases.length === 0 ? (
            <div className="p-12 text-center">
              <Scale className="w-10 h-10 text-stone-300 mx-auto mb-4" />
              <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-stone-500 mb-3">Sin valoraciones todavía</div>
              <h2 className="font-display text-3xl font-black tracking-tight text-[#2d1f1a] mb-3">
                Crea tu primer expediente.
              </h2>
              <p className="text-stone-500 text-sm leading-relaxed mb-6 max-w-md mx-auto">
                Vincula un cliente CRM, sube los documentos (Catastro, VRC, nota simple, edicto…) y prepara la valoración completa.
              </p>
              <Link to="/admin/valoraciones" className="inline-flex items-center gap-2 bg-[#2d1f1a] hover:bg-[#E0651F] text-white px-5 py-3 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors" data-testid="empty-cta">
                <FilePlus2 className="w-4 h-4" /> Empezar ahora
              </Link>
            </div>
          ) : (
            <table className="w-full" data-testid="cases-table">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="text-left text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500 px-6 py-4">Cliente</th>
                  <th className="text-left text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500 px-6 py-4">ID Subasta</th>
                  <th className="text-left text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500 px-6 py-4">Dirección</th>
                  <th className="text-left text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500 px-6 py-4">Estado</th>
                  <th className="text-right text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500 px-6 py-4">Docs</th>
                  <th className="text-left text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500 px-6 py-4">Actualizado</th>
                  <th className="text-right text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500 px-6 py-4">Abrir</th>
                </tr>
              </thead>
              <tbody>
                {cases.map((c, i) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/admin/valoraciones/${c.id}`)}
                    className="border-b border-stone-100 hover:bg-stone-50 fade-up cursor-pointer"
                    style={{ animationDelay: `${i * 30}ms` }}
                    data-testid={`case-row-${c.id}`}
                  >
                    <td className="px-6 py-4">
                      <div className="text-[#2d1f1a] font-medium">{c.client_name_cache || "—"}</div>
                      <div className="text-[10px] text-stone-500 font-mono truncate max-w-[200px]">{c.client_email_cache || ""}</div>
                    </td>
                    <td className="px-6 py-4 font-mono text-xs text-stone-600">{c.auction_id || "—"}</td>
                    <td className="px-6 py-4 text-xs text-stone-600 max-w-[240px] truncate">{c.address || c.cadastral_reference || "—"}</td>
                    <td className="px-6 py-4">
                      <span className={`text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-1 rounded-sm ${STATUS_COLORS[c.status] || "bg-stone-100 text-stone-700"}`}>
                        {meta?.status_labels?.[c.status] || c.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-mono text-sm text-stone-700">{c.document_count || 0}</td>
                    <td className="px-6 py-4 text-xs text-stone-500">{formatDate(c.updated_at)}</td>
                    <td className="px-6 py-4 text-right">
                      <ArrowRight className="w-4 h-4 text-stone-400 inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
